/**
 * Todas as consultas e mutações do app num só lugar, com chaves de cache estáveis.
 * Feature nova: acrescente seu bloco no fim (append-only) e use as chaves de `keys`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AlertList,
  type AuthConfig,
  type ChangePasswordInput,
  type LoginInput,
  type RouteDetail,
  type RouteInput,
  type RouteList,
  type RouteUpdate,
  type SessionResponse,
  type SignupInput,
  type TelegramStatus,
  type Trip,
  type TripInput,
  type TripList,
  type TripUpdate,
  endpoints,
} from '@viaja/contracts';
import { del, get, patch, post } from './api';

export const keys = {
  me: ['me'] as const,
  authConfig: ['auth-config'] as const,
  routes: ['routes'] as const,
  route: (id: string) => ['routes', id] as const,
  trips: ['trips'] as const,
  trip: (id: string) => ['trips', id] as const,
  alerts: ['alerts'] as const,
  telegram: ['telegram'] as const,
};

/* ---------- sessão ---------- */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => get<SessionResponse>(endpoints.me).then((r) => r.user),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useAuthConfig() {
  return useQuery({ queryKey: keys.authConfig, queryFn: () => get<AuthConfig>(endpoints.authConfig) });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => post<SessionResponse>(endpoints.login, input),
    onSuccess: (r) => {
      qc.clear();
      qc.setQueryData(keys.me, r.user);
    },
  });
}

export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SignupInput) => post<SessionResponse>(endpoints.signup, input),
    onSuccess: (r) => {
      qc.clear();
      qc.setQueryData(keys.me, r.user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<void>(endpoints.logout),
    onSettled: () => {
      qc.clear();
      qc.setQueryData(keys.me, null);
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) => post<void>(endpoints.changePassword, input),
  });
}

/* ---------- rotas ---------- */
export function useRoutes(tripId?: string) {
  return useQuery({
    queryKey: tripId ? [...keys.routes, { tripId }] : keys.routes,
    queryFn: () =>
      get<RouteList>(
        tripId ? `${endpoints.routes}?tripId=${encodeURIComponent(tripId)}` : endpoints.routes,
      ).then((r) => r.routes),
  });
}

export function useRoute(id: string) {
  return useQuery({ queryKey: keys.route(id), queryFn: () => get<RouteDetail>(endpoints.route(id)) });
}

function useRouteWrite<V>(fn: (v: V) => Promise<RouteDetail>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (detail) => {
      qc.setQueryData(keys.route(detail.id), detail);
      qc.invalidateQueries({ queryKey: keys.routes, exact: false, refetchType: 'active' });
      qc.invalidateQueries({ queryKey: keys.trips, exact: false });
    },
  });
}

export const useCreateRoute = () =>
  useRouteWrite((input: RouteInput) => post<RouteDetail>(endpoints.routes, input));
export const useUpdateRoute = (id: string) =>
  useRouteWrite((input: RouteUpdate) => patch<RouteDetail>(endpoints.route(id), input));
export const useCheckRoute = (id: string) => useRouteWrite(() => post<RouteDetail>(endpoints.routeCheck(id)));

export function useDeleteRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(endpoints.route(id)),
    onSuccess: (_v, id) => {
      qc.removeQueries({ queryKey: keys.route(id) });
      qc.invalidateQueries({ queryKey: keys.routes });
      qc.invalidateQueries({ queryKey: keys.trips });
    },
  });
}

/* ---------- viagens ---------- */
export function useTrips() {
  return useQuery({
    queryKey: keys.trips,
    queryFn: () => get<TripList>(endpoints.trips).then((r) => r.trips),
  });
}

export function useTrip(id: string) {
  return useQuery({ queryKey: keys.trip(id), queryFn: () => get<Trip>(endpoints.trip(id)) });
}

export function useSaveTrip(id?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TripInput | TripUpdate) =>
      id ? patch<Trip>(endpoints.trip(id), input) : post<Trip>(endpoints.trips, input),
    onSuccess: (trip) => {
      qc.setQueryData(keys.trip(trip.id), trip);
      qc.invalidateQueries({ queryKey: keys.trips });
      qc.invalidateQueries({ queryKey: keys.routes });
    },
  });
}

export function useDeleteTrip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(endpoints.trip(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.trips });
      qc.invalidateQueries({ queryKey: keys.routes });
    },
  });
}

/* ---------- alertas e Telegram ---------- */
export function useAlerts() {
  return useQuery({
    queryKey: keys.alerts,
    queryFn: () => get<AlertList>(endpoints.alerts).then((r) => r.alerts),
  });
}

export function useTelegram() {
  return useQuery({
    queryKey: keys.telegram,
    queryFn: () => get<TelegramStatus>(endpoints.telegram),
    staleTime: 60_000,
  });
}

export function useUnlinkTelegram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => del(endpoints.telegram),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.telegram });
      qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useTestTelegram() {
  return useMutation({ mutationFn: () => post<void>(endpoints.telegramTest) });
}
